import {
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Modal,
  Paper,
  Tooltip,
  Typography,
  useMediaQuery,
} from '@mui/material';
import type { PropsWithChildren } from 'react';
import * as React from 'react';
import { makeStyles } from 'tss-react/mui';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import HelpIcon from '@mui/icons-material/Help';
import { useTranslation } from 'react-i18next';
import CloseIcon from '@mui/icons-material/Close';
import ProgressSteps from '../../../molecules/ProgressSteps/ProgressSteps';
import type { ITheme } from '../../../../theme';

const useStyles = makeStyles()((theme: ITheme) => ({
  modal: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
  },
  root: {
    position: 'relative',
    width: '90%',
    maxWidth: '700px',
    padding: '2em',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    gap: '1em',
    boxShadow: `0px 0px 5px 0 ${theme.palette.primary.main}`,
    [theme.breakpoints.down('sm')]: {
      margin: '1em auto',
      padding: '1em',
    },
    maxHeight: '95vh', // Added to ensure modal doesn't exceed viewport height
  },
  seperator: {
    height: '1px',
    width: '100%',
    background: '#d1cfcf',
    margin: '1em 0',
  },
  header: {
    textAlign: 'center',
    display: 'flex',
    justifyContent: 'center',
    gap: 5,
    alignItems: 'center',
    [theme.breakpoints.down('sm')]: {
      fontSize: '1.1rem',
      gap: 2,
    },
  },
  back: {
    position: 'absolute',
    top: 20,
    left: 0,
    color: '#fff',
    [theme.breakpoints.down('sm')]: {
      top: 10,
    },
  },
  tooltipIcon: {
    color: '#ffffff78',
  },
  content: {
    overflowY: 'auto',
    overflowX: 'clip',
    flexGrow: 1,
    width: '100%',
    maxHeight: '70vh',
  },
  spinner: {
    textAlign: 'center',
    height: '6em',
  },
  close: {
    position: 'absolute',
    right: '1em',
    top: '.8em',
    [theme.breakpoints.down('sm')]: {
      right: '.5em',
      top: '.3em',
    },
  },
  dialogButtons: {
    display: 'flex',
    justifyContent: 'center',
    gap: '1em',
    padding: '1em',
  },
}));

export interface TranscriptionDialogWrapperProps extends PropsWithChildren {
  open: boolean;
  handleClose: () => void;
  currentStep: number;
  title: string;
  helperText: string;
  numberOfSteps: number;
  handleBackClick: () => void;
  submitting: boolean;
}

const TranscriptionDialogWrapper: React.FC<TranscriptionDialogWrapperProps> = ({
  open,
  handleClose,
  currentStep,
  title,
  helperText,
  numberOfSteps,
  children,
  handleBackClick,
  submitting,
}) => {
  const { classes } = useStyles();
  const { t } = useTranslation();
  const isMobile = useMediaQuery((theme: ITheme) => theme.breakpoints.down('sm'));
  const [showClosingDialog, setShowClosingDialog] = React.useState(false);

  const onClose = () => {
    setShowClosingDialog(true);
  };
  return (
    <>
      <Modal
        open={open}
        className={classes.modal}
        onClose={onClose}
        slotProps={{ backdrop: { sx: { backgroundColor: '#000000d6' } } }}
      >
        <Paper className={classes.root}>
          <ProgressSteps currentStep={currentStep} numberOfSteps={numberOfSteps} />
          <IconButton
            aria-label="Close transcription dialog"
            onClick={onClose}
            id={`btn_wizard_close_${currentStep}`}
            className={classes.close}
          >
            <CloseIcon />
          </IconButton>
          <Typography className={classes.header} variant="h6">
            {title}
            <Tooltip title={helperText} arrow placement="top">
              <HelpIcon className={classes.tooltipIcon} />
            </Tooltip>
          </Typography>

          <div className={classes.seperator} />
          <div className={classes.content}>
            {submitting ? (
              <div className={classes.spinner}>
                <CircularProgress size={50} color="primary" />
              </div>
            ) : (
              children
            )}
          </div>
          {currentStep > 0 && (
            <Button
              id={`btn_wizard_back_${currentStep}`}
              className={classes.back}
              onClick={handleBackClick}
              color="primary"
            >
              <ArrowBackIcon />
              {!isMobile && t('home.input.dialog.back')}
            </Button>
          )}
        </Paper>
      </Modal>
      <Dialog onClose={() => setShowClosingDialog(false)} open={showClosingDialog}>
        <DialogContent>
          <DialogTitle>{t('home.input.dialog.close.title')}</DialogTitle>
          <div className={classes.dialogButtons}>
            <Button
              variant="contained"
              id={`btn_transcription_close_yes_${currentStep}`}
              onClick={() => {
                setShowClosingDialog(false);
                handleClose();
              }}
            >
              {t('home.input.dialog.close.yes')}
            </Button>
            <Button
              variant="contained"
              id="btn_transcription_close_no"
              onClick={() => setShowClosingDialog(false)}
            >
              {t('home.input.dialog.close.no')}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default TranscriptionDialogWrapper;
